import { Cross2Icon } from "@radix-ui/react-icons";
import { WpHeaderNavResponse, childItem } from "@/lib/wordpress/getMenusFromWP";
import AccordionContent from "@/components/AccordionContent";
import EventLink from "@/components/Link/EventLink/EventLink";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/AuthProvider";
import { Badge } from "@/components/ui/badge";
import { useRef } from "react";

import { useLogistrationModal } from "@/components/LogistrationModal/hooks/useLogistrationModal";
import { LearnerDashboardCTATestResult } from "@/monarch/rules/learnerDashboardCTA/types";
import useLmsUserStatus from "@/lib/lms/getLmsUserStatus";
import MonarchResolver from "@/monarch/MonarchResolver";
import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import { transformSearchUrl } from "../utils/transformSearchUrl";
import { useAuthxNavUrls } from "@/hooks/useAuthxNavUrls";

type Props = {
  headerNavData: WpHeaderNavResponse;
  onOpenChange: (open: boolean) => void;
};

// Helper function to process sections
const processSections = (
  originalSections: { label: string; childItems: { nodes: childItem[] } }[],
) => {
  return originalSections;
};

const renderMenuItemsGrouped = (
  items: childItem[],
  isLastSection: boolean = false,
  lastLinkRef?: React.RefObject<HTMLAnchorElement | null>,
  onTabFromLastLink?: () => void,
  transformUrl?: (url: string) => string,
): JSX.Element[] => {
  const elements: JSX.Element[] = [];
  let currentGroup: JSX.Element[] = [];
  let currentGroupTitle: string | null = null;

  const flushCurrentGroup = () => {
    if (currentGroup.length > 0) {
      if (currentGroupTitle) {
        // Add the group title and wrap the links in a <ul>
        elements.push(
          <li
            key={currentGroupTitle}
            className="font-bold text-xs !mt-4 list-none"
          >
            {currentGroupTitle}
          </li>,
        );
        elements.push(
          <ul
            key={`${currentGroupTitle}-list`}
            className="flex flex-col space-y-2 ml-0"
          >
            {currentGroup}
          </ul>,
        );
      } else {
        // No group title, add items directly
        elements.push(...currentGroup);
      }
      currentGroup = [];
      currentGroupTitle = null;
    }
  };

  items.forEach((item, index) => {
    const isLastLink = isLastSection && index === items.length - 1;

    if (item.label.startsWith("Browse by") || item.label.startsWith("Earn a")) {
      // Flush any current group before starting a new section
      flushCurrentGroup();

      elements.push(
        <li
          key={`${item.label}-${index}`}
          className="text-base font-bold list-none"
        >
          {item.label}
        </li>,
      );
    } else if (item.label.startsWith("Explore career resources")) {
      // Flush any current group before starting a new section
      flushCurrentGroup();

      elements.push(
        <li
          key={`${item.label}-${index}`}
          className="text-base font-bold !mb-2 !mt-8 list-none"
        >
          {item.label}
        </li>,
      );
    } else if (
      item.label.startsWith("Popular") ||
      item.label.startsWith("For beginners")
    ) {
      // Flush any current group before starting a new subsection
      flushCurrentGroup();

      // Set this as the current group title
      currentGroupTitle = item.label;
    } else {
      // This is a regular link item
      const extraClasses = item.label.startsWith("View all")
        ? "underline hover:text-info-500"
        : "hover:underline";

      const extraLiClasses = item.label.startsWith("View all") ? "!mt-4" : "";

      let linkElement;
      if (isLastLink && lastLinkRef && onTabFromLastLink) {
        linkElement = (
          <li
            key={`${item.label}-${index}`}
            className={`list-none ${extraLiClasses}`}
          >
            <span
              ref={lastLinkRef}
              onKeyDown={(e) => {
                if (e.key === "Tab") {
                  e.preventDefault();
                  onTabFromLastLink();
                }
              }}
              tabIndex={0}
            >
              <EventLink
                class_name={`text-sm ${extraClasses}`}
                href={transformUrl ? transformUrl(item.url) : item.url}
                text={item.label}
                position={Number(index + 1).toString()}
                location="nav-dropdown"
                html_id="nav-bar-link"
                element_type="entry"
              />
            </span>
          </li>
        );
      } else {
        linkElement = (
          <li
            key={`${item.label}-${index}`}
            className={`list-none ${extraLiClasses}`}
          >
            <EventLink
              class_name={`text-sm ${extraClasses}`}
              href={transformUrl ? transformUrl(item.url) : item.url}
              text={item.label}
              position={Number(index + 1).toString()}
              location="nav-dropdown"
              html_id="nav-bar-link"
              element_type="entry"
            />
          </li>
        );
      }

      // Add to current group if we have a group title, otherwise add directly
      if (currentGroupTitle) {
        currentGroup.push(linkElement);
      } else {
        elements.push(linkElement);
      }
    }
  });

  // Flush any remaining group
  flushCurrentGroup();

  return elements;
};

export default function Links({ headerNavData, onOpenChange }: Props) {
  const { isLoggedIn } = useAuth();
  const lastLinkRef = useRef<HTMLAnchorElement | null>(null);
  const { lmsUserStatus } = useLmsUserStatus();
  const { handleLogistrationModalClick, handleLogistrationModalKeyDown } =
    useLogistrationModal();
  const { navSignInHref, navRegisterHref } = useAuthxNavUrls();

  const activeIndex = PRODUCT_INDEX_EN;

  // Transform search URLs to use correct facet names based on active index
  const transformUrl = (url: string): string => {
    return transformSearchUrl(url, activeIndex);
  };

  const handleCloseModalAndFocusSearch = () => {
    // Close the modal
    onOpenChange(false);

    // Focus the search input after a short delay to ensure modal is closed
    setTimeout(() => {
      const searchInput = document.querySelector(
        'input[type="search"]',
      ) as HTMLInputElement;
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  };

  // Process the sections using the helper function
  const sections = processSections(
    headerNavData.menus.nodes[0].menuItems.nodes[0].childItems.nodes,
  );

  // Create mobile sections by splitting the last section at "Career Resources"
  const mobileSections = (() => {
    const sectionWithCareerResources = sections.find((section) => {
      return section.childItems.nodes.some((item) => {
        return (
          item.label === "Explore career resources" ||
          item.label === "Explore Career resources"
        );
      });
    });

    if (!sectionWithCareerResources) {
      return sections;
    }

    const careerResourcesIndex =
      sectionWithCareerResources?.childItems.nodes.findIndex(
        (item) =>
          item.label === "Explore career resources" ||
          item.label === "Explore Career resources",
      );

    if (!careerResourcesIndex || careerResourcesIndex === -1) {
      // If Career Resources not found, return sections as-is
      return sections;
    }

    // Split the last section at Career Resources
    const beforeCareerResources =
      sectionWithCareerResources.childItems.nodes.slice(
        0,
        careerResourcesIndex,
      );
    const careerResourcesAndAfter =
      sectionWithCareerResources.childItems.nodes.slice(
        careerResourcesIndex + 1,
      ); // Skip the Career Resources item itself

    const originalSectionIndex = sections.findIndex(
      (section) => section.label === sectionWithCareerResources.label,
    );

    const result = [
      ...sections.slice(0, originalSectionIndex), // Everything before the original section
      {
        ...sectionWithCareerResources,
        childItems: {
          nodes: beforeCareerResources,
        },
      },
      {
        label: "Explore career resources",
        childItems: {
          nodes: careerResourcesAndAfter,
        },
      },
      ...sections.slice(originalSectionIndex + 1), // Everything after the original section
    ];

    return result;
  })();

  return (
    <>
      {/* Close button positioned over hamburger menu - only visible on mobile */}
      <button
        onClick={() => onOpenChange(false)}
        className="md:hidden absolute -top-14 left-4 z-20 w-10 h-10 flex items-center justify-center bg-white rounded-full hover:bg-gray-100"
        aria-label="Close menu"
      >
        <Cross2Icon className="w-6 h-6" />
      </button>

      <div className="hidden lg:flex justify-center space-x-8 w-full py-10 px-8">
        {/* Render main sections */}
        {sections.map((section, index) => {
          const isLastSection = index === sections.length - 1;
          return (
            <div
              className="flex flex-col space-y-4 w-[266px]"
              key={`${section.label}-${index}`}
            >
              <div className="text-base font-bold">{section.label}</div>
              <ul
                className="flex flex-col space-y-2"
                key={`${section.label}-${index}`}
              >
                {renderMenuItemsGrouped(
                  section.childItems.nodes,
                  isLastSection,
                  lastLinkRef,
                  handleCloseModalAndFocusSearch,
                  transformUrl,
                )}
              </ul>
            </div>
          );
        })}
      </div>
      <div className="flex lg:hidden flex-col w-full h-screen max-h-screen">
        {/* Sticky sign-in/register section */}
        <MonarchResolver<LearnerDashboardCTATestResult>
          experimentKey="useLearnerDashboardCTA"
          variants={{
            control: isLoggedIn &&
              !lmsUserStatus?.isEnterpriseUser &&
              !lmsUserStatus?.isEmailConfirmationNeeded && (
                <>
                  <div className="sticky top-0 z-10 bg-white border-b border-putty-400 flex justify-center">
                    <EventLink
                      href={"https://home.edx.org"}
                      text={"Dashboard"}
                      html_id="dashboard"
                      location={"nav-dropdown"}
                      name="dashboard"
                      class_name="h-10 text-base w-full py-4 my-2 mx-4 focus-visible:ring-offset-2 focus-visible:ring-2 focus-visible:ring-primary-500"
                      button_variant="default"
                    />
                  </div>
                </>
              ),
            show: isLoggedIn &&
              !lmsUserStatus?.isEnterpriseUser &&
              !lmsUserStatus?.isEmailConfirmationNeeded && (
                <>
                  <div className="sticky top-0 z-10 bg-white border-b border-putty-400 flex justify-center">
                    <EventLink
                      href={"/dashboard"}
                      text={"Dashboard"}
                      html_id="dashboard"
                      location={"nav-dropdown"}
                      name="dashboard"
                      class_name="h-10 text-base w-full py-4 my-2 mx-4 focus-visible:ring-offset-2 focus-visible:ring-2 focus-visible:ring-primary-500"
                      button_variant="default"
                    />
                  </div>
                  <div className="sticky top-0 z-10 bg-white border-b border-putty-400 pl-4 flex items-center font-normal text-sm py-3 space-x-2 max-h-[44px]">
                    <span className="text-sm">
                      Your Learner&apos;s Dashboard
                    </span>
                    <Badge className="px-2 h-4 bg-accent-yellow [border-radius:4px] font-normal">
                      Beta
                    </Badge>
                  </div>
                </>
              ),
          }}
        />
        {!isLoggedIn && (
          <div className="md:hidden sticky top-0 z-10 bg-white border-b border-putty-400 flex justify-between space-x-2 p-2">
            <EventLink
              href={navSignInHref}
              text={"Sign in"}
              html_id="sign-in"
              location={"nav-dropdown"}
              name="sign-in"
              class_name="h-10 w-1/2 text-base py-5"
              button_variant="brandOutline"
              customOnClick={(e) => handleLogistrationModalClick(e, "signin")}
            />
            <Button
              variant="brand"
              className="h-10 w-1/2 text-base py-5"
              onClick={(e) => handleLogistrationModalClick(e, "register")}
              onKeyDown={(e) => handleLogistrationModalKeyDown(e, "register")}
              asChild
            >
              <a aria-label={"Register"} href={navRegisterHref}>
                Register for free
              </a>
            </Button>
          </div>
        )}

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto pb-20 w-full min-h-0 overscroll-contain">
          {/* Render main sections */}
          {mobileSections.map((section, index) => {
            const isLastAccordion = index === mobileSections.length - 1;

            const accordionElement = (
              <AccordionContent
                title={section.label}
                rootClassName="w-full border-b border-putty-400 !mt-0"
                iconClassName="text-primary group-hover:text-white group-[&[data-state=open]]:text-primary"
                contentClassName="font-normal"
                childrenClassName="pl-4 py-4"
                triggerClassName="font-normal mt-0 px-4 py-3 text-sm !bg-white [&[data-state=open]]:!bg-putty-200 [&[data-state=open]]:!text-primary hover:!bg-primary hover:!text-white group"
                key={`${section.label}-${index}`}
              >
                <ul className="flex flex-col space-y-3">
                  {renderMenuItemsGrouped(
                    section.childItems.nodes.slice(0),
                    index === mobileSections.length - 1,
                    lastLinkRef,
                    undefined,
                    transformUrl,
                  )}
                </ul>
              </AccordionContent>
            );

            if (isLastAccordion) {
              return (
                <div
                  key={`${section.label}-${index}`}
                  onKeyDown={(e) => {
                    if (e.key === "Tab" && !e.shiftKey) {
                      // Check if the accordion is closed by looking at data-state
                      const trigger =
                        e.currentTarget.querySelector("[data-state]");
                      if (
                        trigger &&
                        trigger.getAttribute("data-state") !== "open"
                      ) {
                        e.preventDefault();
                      }
                    }
                  }}
                >
                  {accordionElement}
                </div>
              );
            }

            return accordionElement;
          })}
        </div>
      </div>
    </>
  );
}
